import { create } from 'zustand';
import { userClient } from '@/services/api/clients/user';
import { isPublicAgentSurface } from '@/services/api/apiConfig';
import { authService } from '@/services/authService';
import { announceSessionUser, setExpectedSessionUser } from '@/services/sessionIdentity';
import { useHubStore } from '@/app/(hub)/_stores/useHubStore';
import { chatClient } from '@/services/api/clients/chat';
import { setLoggedInUserId, clearLoggedInUserId, getTrackingId } from '@/utils/userTracking';
import { queryClient } from '@/providers/queryClient';
import posthog from 'posthog-js';
import type { MfaStatus } from '@/services/api/clients/mfa';

export type UserData = {
  id: string;
  uuid: string;
  username?: string;
  email?: string;
  is_superuser: boolean;
  synced?: boolean;
  profile?: {
    id: number;
    first_name: string;
    last_name: string;
    openai_api_key: string | null;
    user: string;
    job_title?: string;
    phone?: string;
    /** Set when the number was proven with a one-time code; null = self-asserted. */
    phone_verified_at?: string | null;
    profile_picture_url?: string;
  };
  /** MFA enrollment state; absent on responses predating the enrollment feature. */
  mfa?: MfaStatus;
  org_permissions: Array<{
    uuid: string;
    level: string;
  }>;
  location_permissions: Array<{
    uuid: string;
    level: string;
  }>;
};

/**
 * Which uuid PostHog currently has identified. Persisted rather than held in a module
 * variable because a forced logout — apiConfig.redirectToLogin(), when a token turns out
 * to be dead — hard-navigates without ever running logout(). A module variable is always
 * null after that reload while PostHog's own persistence still holds the previous user,
 * so the next user's identify() would be declined and their events would land on the
 * previous user's person record. On a shared clinic workstation that misattributes one
 * clinician's activity to another.
 */
const IDENTIFIED_UUID_KEY = 'posthog_identified_uuid';

/** In-memory only: avoids redundant identify() calls within a single page load. */
let identitySignature: string | null = null;

function readIdentifiedUuid(): string | null {
  try {
    return localStorage.getItem(IDENTIFIED_UUID_KEY);
  } catch {
    return null;
  }
}

function writeIdentifiedUuid(uuid: string | null): void {
  try {
    if (uuid) localStorage.setItem(IDENTIFIED_UUID_KEY, uuid);
    else localStorage.removeItem(IDENTIFIED_UUID_KEY);
  } catch {
    // Storage unavailable (private mode / quota). Identity simply isn't deduped.
  }
}

function buildPersonProperties(data: UserData): Record<string, string | boolean> {
  const properties: Record<string, string | boolean> = { is_superuser: data.is_superuser };
  if (data.email) properties.email = data.email;
  if (data.username) properties.username = data.username;
  const name = [data.profile?.first_name, data.profile?.last_name].filter(Boolean).join(' ');
  if (name) properties.name = name;
  return properties;
}

function syncPostHogIdentity(data: UserData): void {
  const previouslyIdentified = readIdentifiedUuid();

  // Only on a genuine user switch. Resetting before a first-time identify would discard
  // the anonymous id and break anonymous→signup funnel stitching.
  if (previouslyIdentified && previouslyIdentified !== data.uuid) {
    posthog.reset();
    identitySignature = null;
  }

  // Deliberately NOT gated on uuid alone. setUserData runs twice per login (the thin
  // login response, then the full user/data/ payload) and again after profile edits, so
  // a uuid gate would pin person properties to the thinnest payload forever — during
  // registration that means a nameless person for the whole first session, since
  // RegisterPage logs in at step 1 before the profile exists.
  const personProperties = buildPersonProperties(data);
  const signature = `${data.uuid}:${JSON.stringify(personProperties)}`;
  if (identitySignature !== signature) {
    posthog.identify(data.uuid, personProperties);
    identitySignature = signature;
    writeIdentifiedUuid(data.uuid);
  }
}

/** Clears the PostHog identity. Every session-ending path must call this. */
function resetPostHogIdentity(): void {
  posthog.reset();
  identitySignature = null;
  writeIdentifiedUuid(null);
}

interface UserStoreState {
  user: UserData | null;
  isSuperuser: boolean;
  isLoading: boolean;
  setUserData(data: UserData): void;
  fetchAndSetUserData(loginResponseData?: UserData): Promise<void>;
  initialize(): Promise<void>;
  logout(): Promise<void>;
}

export const useUserStore = create<UserStoreState>()((set, get) => ({
  user: null,
  isSuperuser: false,
  isLoading: true,

  setUserData: (data: UserData) => {
    set({ user: data, isSuperuser: data.is_superuser });
    if (data.uuid) {
      // The identity guard in fetchWithAuth compares every request's token
      // against the user this tab renders (wrong-user write protection).
      setExpectedSessionUser(data.uuid);
      const currentId = getTrackingId();
      if (currentId && currentId !== data.uuid) {
        chatClient
          .updateAnonymousSession({ anonymous_id: currentId })
          .catch((err) => console.error('Failed to backfill anonymous sessions:', err));
      }
      setLoggedInUserId(data.uuid);
      syncPostHogIdentity(data);
    }
  },

  fetchAndSetUserData: async (loginResponseData?: UserData) => {
    if (loginResponseData) {
      get().setUserData(loginResponseData);
    }
    try {
      const response = await userClient.getUserData();
      if (response.data) {
        get().setUserData(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      if (!loginResponseData) {
        authService.clearTokens();
      }
    }
  },

  initialize: async () => {
    const token = authService.getToken();
    if (!token) {
      set({ isLoading: false });
      return;
    }
    // Never bootstrap the Hub user on a public visitor surface. fetchWithAuth is
    // anonymous there (ALI-2310), so `user/data/` can ONLY 401 — and the catch in
    // fetchAndSetUserData reads that as a dead session and clears the cookies.
    // That is what logged signed-in users out when they opened an SMS deep link:
    // the /s/ redirector runs this bootstrap, wipes the session, then hard-loads
    // the Hub page, which finds no cookie and bounces to /login.
    // Same guard as useFeatureFlags.
    if (isPublicAgentSurface()) {
      set({ isLoading: false });
      return;
    }
    await get().fetchAndSetUserData();
    set({ isLoading: false });
  },

  logout: async () => {
    const { user } = get();
    if (user?.id) {
      const { clearPersistedFilters } = await import('@/services/filterPersistenceService');
      clearPersistedFilters(user.id);
    }
    if (user?.uuid) {
      // Signing out ends the session. sessionStorage survives it (it only clears when
      // the tab closes), so without this the MFA prompt would stay suppressed through
      // a logout/login cycle in the same tab.
      const { clearMfaPromptSeen } = await import('@/utils/mfaSession');
      clearMfaPromptSeen(user.uuid);
    }
    useHubStore.getState().resetSelection();
    authService.clearTokens();
    // Tokens are browser-wide: this tab no longer renders anyone, and other
    // Hub tabs must learn the session ended instead of impersonating it.
    setExpectedSessionUser(null);
    announceSessionUser(null);
    resetPostHogIdentity();
    clearLoggedInUserId();
    // Drop all cached query data — lead detail etc. can hold PHI and must not leak
    // into the next user's session on a shared workstation.
    queryClient.clear();
    set({ user: null, isSuperuser: false });
    window.location.assign('/login/');
  },
}));
